/**
 * keyboardModifierState — Shared modifier definitions, event reconciliation,
 * modifier satisfaction evaluation, and gameplay action classification.
 *
 * Extracted as a shared seam to prevent circular imports between
 * keyboardChordMatcher and heldAimInput.
 */

export const MODIFIER_KEY_CODES = {
  SHIFT_LEFT: 'ShiftLeft',
  SHIFT_RIGHT: 'ShiftRight',
  CONTROL_LEFT: 'ControlLeft',
  CONTROL_RIGHT: 'ControlRight',
  ALT_LEFT: 'AltLeft',
  ALT_RIGHT: 'AltRight',
} as const;

export type ModifierFamily = 'shift' | 'ctrl' | 'alt';

/**
 * Authoritative modifier flags carried by standard DOM KeyboardEvents.
 */
export interface ModifierEventFlags {
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
}

/**
 * Reconcile the tracked modifier keys against the event's authoritative
 * modifier flags. A modifier no longer physically held is removed from
 * keysDown so a swallowed keyup (focus traversal, alt-tab) can't strand it
 * and lock out all unmodified input.
 */
export function reconcileModifierKeys(
  keysDown: Set<string>,
  e: ModifierEventFlags
): void {
  if (!e.shiftKey) {
    keysDown.delete(MODIFIER_KEY_CODES.SHIFT_LEFT);
    keysDown.delete(MODIFIER_KEY_CODES.SHIFT_RIGHT);
  }
  if (!e.ctrlKey) {
    keysDown.delete(MODIFIER_KEY_CODES.CONTROL_LEFT);
    keysDown.delete(MODIFIER_KEY_CODES.CONTROL_RIGHT);
  }
  if (!e.altKey) {
    keysDown.delete(MODIFIER_KEY_CODES.ALT_LEFT);
    keysDown.delete(MODIFIER_KEY_CODES.ALT_RIGHT);
  }
}

/**
 * Maps a DOM key code to its modifier family ('shift' | 'ctrl' | 'alt'), or null if not a modifier.
 */
export function getModifierFamilyFromCode(code: string): ModifierFamily | null {
  if (code === MODIFIER_KEY_CODES.SHIFT_LEFT || code === MODIFIER_KEY_CODES.SHIFT_RIGHT) {
    return 'shift';
  }
  if (code === MODIFIER_KEY_CODES.CONTROL_LEFT || code === MODIFIER_KEY_CODES.CONTROL_RIGHT) {
    return 'ctrl';
  }
  if (code === MODIFIER_KEY_CODES.ALT_LEFT || code === MODIFIER_KEY_CODES.ALT_RIGHT) {
    return 'alt';
  }
  return null;
}

/**
 * Evaluates whether a modifier requirement is met by the currently held keys.
 * If no modifier is specified, requires that no unconsumed modifier is pressed.
 * An active held gameplay modifier (e.g. free-aim Alt or Shift) can be passed as `consumedModifier`,
 * which prevents that modifier from blocking unmodified gameplay actions or competing chords.
 */
export function isModifierSatisfied(
  modifier: string | undefined,
  keysDown: ReadonlySet<string>,
  consumedModifier?: ModifierFamily | null
): boolean {
  const shiftDown = keysDown.has(MODIFIER_KEY_CODES.SHIFT_LEFT) || keysDown.has(MODIFIER_KEY_CODES.SHIFT_RIGHT);
  const ctrlDown = keysDown.has(MODIFIER_KEY_CODES.CONTROL_LEFT) || keysDown.has(MODIFIER_KEY_CODES.CONTROL_RIGHT);
  const altDown = keysDown.has(MODIFIER_KEY_CODES.ALT_LEFT) || keysDown.has(MODIFIER_KEY_CODES.ALT_RIGHT);

  if (!modifier) {
    if (shiftDown && consumedModifier !== 'shift') return false;
    if (ctrlDown && consumedModifier !== 'ctrl') return false;
    if (altDown && consumedModifier !== 'alt') return false;
    return true;
  }

  switch (modifier) {
    case 'shift':
      if (!shiftDown) return false;
      if (ctrlDown && consumedModifier !== 'ctrl') return false;
      if (altDown && consumedModifier !== 'alt') return false;
      return true;
    case 'ctrl':
      if (!ctrlDown) return false;
      if (shiftDown && consumedModifier !== 'shift') return false;
      if (altDown && consumedModifier !== 'alt') return false;
      return true;
    case 'alt':
      if (!altDown) return false;
      if (shiftDown && consumedModifier !== 'shift') return false;
      if (ctrlDown && consumedModifier !== 'ctrl') return false;
      return true;
    default:
      return true;
  }
}

/**
 * Returns true if an element is an input, textarea, or contentEditable element.
 */
export function isTextFieldElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  const htmlEl = el as HTMLElement;
  return Boolean(htmlEl.isContentEditable || htmlEl.contentEditable === 'true');
}

export const GAMEPLAY_ACTION_NAMES = Object.freeze([
  'move_left',
  'move_right',
  'move_up',
  'move_down',
  'jump',
  'jump_alt',
  'basic_attack',
  'secondary_attack',
  'bar1_slot1',
  'bar1_slot2',
  'bar1_slot3',
  'bar1_slot4',
  'bar1_slot5',
  'bar1_slot6',
  'bar1_slot7',
  'bar1_slot8',
  'bar1_slot9',
  'bar1_slot10',
] as const);

export const GAMEPLAY_ACTIONS = new Set<string>(GAMEPLAY_ACTION_NAMES);

/**
 * Returns true if the action is a movement, jump, attack, or ability hotbar action.
 */
export function isGameplayAction(action: string): boolean {
  if (GAMEPLAY_ACTIONS.has(action)) return true;
  if (/^bar\d+_(slot|page_)/.test(action) || action.startsWith('move_') || action.startsWith('jump')) {
    return true;
  }
  return false;
}
